/**
 * seedWorkflows.ts — the two pre-built example workflows offered from the
 * empty state so a brand-new org can explore the feature immediately.
 * Both are created `enabled:false` — activation still goes through the
 * real double-confirm, deliberately.
 */

import type { WorkflowEdge, WorkflowNode } from "../workflow.types";

let seedIdCounter = 0;
function seedId(prefix: string) {
  seedIdCounter += 1;
  return `seed-${prefix}-${Date.now()}-${seedIdCounter}`;
}

interface SeedWorkflow {
  name: string;
  description: string;
  trigger: { type: string; config?: Record<string, unknown> };
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
}

function chainEdge(source: string, target: string): WorkflowEdge {
  return { id: seedId("edge"), source, sourcePort: "out", target, targetPort: "in" };
}

function buildFollowUpSeed(): SeedWorkflow {
  const triggerId = seedId("node");
  const fileNoteId = seedId("node");
  const emailId = seedId("node");
  const goalsId = seedId("node");
  const todoId = seedId("node");

  const nodes: WorkflowNode[] = [
    { id: triggerId, type: "trigger", subtype: "meeting_filenote_completed", x: 80, y: 200, config: { label: "Meeting file note completed" } },
    { id: fileNoteId, type: "action", subtype: "generate_file_note", x: 380, y: 200, config: { label: "Generate file note" } },
    { id: emailId, type: "action", subtype: "draft_client_email", x: 680, y: 200, config: { label: "Draft client email — Meeting summary" } },
    { id: goalsId, type: "action", subtype: "create_document", x: 980, y: 200, config: { label: "Create goals list", args: { documentType: "goals" } } },
    { id: todoId, type: "action", subtype: "create_document", x: 1280, y: 200, config: { label: "Create to-do list", args: { documentType: "todo" } } },
  ];

  const edges: WorkflowEdge[] = [
    chainEdge(triggerId, fileNoteId),
    chainEdge(fileNoteId, emailId),
    chainEdge(emailId, goalsId),
    chainEdge(goalsId, todoId),
  ];

  return {
    name: "Post-meeting follow-up",
    description: "Generates a file note, drafts the client summary email, and creates goals and to-do lists after every meeting.",
    trigger: { type: "meeting_filenote_completed" },
    nodes,
    edges,
  };
}

function buildRetirementSeed(): SeedWorkflow {
  const triggerId = seedId("node");
  const fileNoteId = seedId("node");
  const conditionId = seedId("node");
  const retirementEmailId = seedId("node");
  const standardEmailId = seedId("node");

  const nodes: WorkflowNode[] = [
    { id: triggerId, type: "trigger", subtype: "meeting_filenote_completed", x: 80, y: 300, config: { label: "Meeting file note completed" } },
    { id: fileNoteId, type: "action", subtype: "generate_file_note", x: 380, y: 300, config: { label: "Generate file note" } },
    {
      id: conditionId,
      type: "condition",
      subtype: "condition",
      x: 680,
      y: 300,
      config: {
        label: "Last meeting before retirement?",
        prompt: "If this was the client's last scheduled meeting before retirement, take Yes; otherwise take No.",
        yesLabel: "Yes",
        noLabel: "No",
      },
    },
    { id: retirementEmailId, type: "action", subtype: "draft_client_email", x: 1000, y: 160, config: { label: "Draft retirement closing email" } },
    { id: standardEmailId, type: "action", subtype: "draft_client_email", x: 1000, y: 440, config: { label: "Draft standard follow-up email" } },
  ];

  const edges: WorkflowEdge[] = [
    chainEdge(triggerId, fileNoteId),
    chainEdge(fileNoteId, conditionId),
    { id: seedId("edge"), source: conditionId, sourcePort: "yes", target: retirementEmailId, targetPort: "in" },
    { id: seedId("edge"), source: conditionId, sourcePort: "no", target: standardEmailId, targetPort: "in" },
  ];

  return {
    name: "Retirement milestone check",
    description: "Branches on whether this was the client's last meeting before retirement and sends the matching follow-up email.",
    trigger: { type: "meeting_filenote_completed" },
    nodes,
    edges,
  };
}

export function buildSeedWorkflows(): SeedWorkflow[] {
  return [buildFollowUpSeed(), buildRetirementSeed()];
}
